import { useEffect, useRef, useState, useCallback } from "react";
import { loadModules, setDefaultOptions } from "esri-loader";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { RotateCcw, Lock, X } from "lucide-react";
import * as turf from "@turf/turf";
import { WaypointInfoPanel, WaypointInfo, getWaypointIcon, getWaypointLabel } from "./WaypointInfoPanel";

interface RunHeroMapProps {
  runId: string;
  runTitle?: string;
  equipmentType?: string;
  heroPhotoUrl?: string | null;
  className?: string;
  highlightedSegmentId?: string | null;
  initialCenter?: [number, number];
  initialExtent?: { xmin: number; ymin: number; xmax: number; ymax: number };
  waypoints?: any[]; // Pre-fetched waypoints to avoid caching issues
  onPhotoClick?: (url: string) => void;
  isInteractive?: boolean; // Controls whether map can be panned/zoomed
}

const RunHeroMap = ({
  runId,
  heroPhotoUrl,
  initialCenter,
  initialExtent,
  waypoints,
  className = "",
  onPhotoClick,
  isInteractive = true,
}: RunHeroMapProps) => {
  const mapDiv = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [animationReady, setAnimationReady] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [distance, setDistance] = useState<number | null>(null);
  const [showReplay, setShowReplay] = useState(false);
  const [currentWaypoint, setCurrentWaypoint] = useState<WaypointInfo | null>(null);
  const [showWaypointPanel, setShowWaypointPanel] = useState(false);
  const [hoverWaypoint, setHoverWaypoint] = useState<{
    name: string;
    description?: string;
    detection_type?: string;
    x: number;
    y: number;
  } | null>(null);
  const [clickedWaypoint, setClickedWaypoint] = useState<{
    name: string;
    description?: string;
    detection_type?: string;
    photo_urls?: string[];
    x: number;
    y: number;
  } | null>(null);
  const animationRef = useRef<number | null>(null);
  const animateFunctionRef = useRef<(() => void) | null>(null);
  const currentQueryIdRef = useRef(0);
  const trackFeaturesRef = useRef<any[]>([]); // Store all track features for client-side filtering
  const waypointsDataRef = useRef<any[]>([]); // Store waypoints for tracking during animation
  const shownWaypointIdsRef = useRef<Set<string>>(new Set()); // Track which waypoints have been shown
  const animationStartTimeRef = useRef<number>(0); // Track animation start time for elapsed calculation
  const waypointsLayerRef = useRef<any>(null); // Store waypoints layer for hover detection

  useEffect(() => {
    if (!mapDiv.current || !runId) return;

    // Use AMD loader from the ArcGIS CDN
    setDefaultOptions({ version: "4.33" });

    let view: any = null;

    const initializeMap = async () => {
      try {
        const [Map, MapView, GeoJSONLayer, VectorTileLayer, GraphicsLayer, Graphic, Zoom, BasemapToggle, Basemap] =
          await loadModules(
            [
              "esri/Map",
              "esri/views/MapView",
              "esri/layers/GeoJSONLayer",
              "esri/layers/VectorTileLayer",
              "esri/layers/GraphicsLayer",
              "esri/Graphic",
              "esri/widgets/Zoom",
              "esri/widgets/BasemapToggle",
              "esri/Basemap",
            ],
            { css: true },
          );

        // Helper: fade the layer
        function fadeLayer(layer: any, targetOpacity = 0.5, duration = 1500) {
          const startOpacity = layer.opacity ?? 1;
          const start = performance.now();

          function step(now: number) {
            const progress = Math.min((now - start) / duration, 1);
            layer.opacity = startOpacity + (targetOpacity - startOpacity) * progress;
            if (progress < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        }

        // Create the run track layer with temporal data
        const runTrackLayer = new GeoJSONLayer({
          url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-tracks-geojson?run_id=${runId}&lines=true`,
          title: "Run Track",
          timeInfo: {
            startField: "start_timestamp",
            endField: "end_timestamp",
            trackIdField: "run_id",
          },
          effect: "bloom(2, 1px, 10%)",
          renderer: {
            type: "simple",
            symbol: {
              type: "simple-line",
              color: [255, 198, 102, 1],
              width: 3,
            },
          },
          opacity: 0, // Start invisible
        });

        // Simple circle waypoint renderer
        const waypointRenderer = {
          type: "simple",
          symbol: {
            type: "simple-marker",
            size: 8,
            color: [255, 255, 255, 1],
            outline: {
              color: [255, 198, 102, 1], // brand gold
              width: 2,
            },
          },
        };

        // Create waypoints layer from pre-fetched data or edge function
        let waypointsLayer;
        if (waypoints && waypoints.length > 0) {
          // Convert waypoints to GeoJSON and create blob URL
          const waypointsGeoJSON = {
            type: "FeatureCollection",
            features: waypoints.map((wp: any) => ({
              type: "Feature",
              geometry: wp.geom,
              properties: {
                id: wp.id,
                name: wp.name || "Unnamed Waypoint",
                description: wp.description,
                recorded_at: wp.recorded_at ? new Date(wp.recorded_at).getTime() : null,
                auto_generated: wp.auto_generated,
                detection_type: wp.detection_type,
                symbol: wp.symbol,
                photo_urls: wp.photo_urls,
              },
            })),
          };

          const blob = new Blob([JSON.stringify(waypointsGeoJSON)], { type: "application/json" });
          const blobUrl = URL.createObjectURL(blob);

          waypointsLayer = new GeoJSONLayer({
            url: blobUrl,
            title: "Run Waypoints",
            outFields: ["*"],
            timeInfo: {
              startField: "recorded_at",
              endField: "recorded_at",
            },
            effect: "drop-shadow(1.5px, 1.5px, 2.25px, rgb(50,50,50))",
            renderer: waypointRenderer,
          });
          waypointsLayerRef.current = waypointsLayer;
        } else {
          // Fallback to edge function if waypoints not provided
          waypointsLayer = new GeoJSONLayer({
            url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-waypoints-geojson?run_id=${runId}`,
            title: "Run Waypoints",
            outFields: ["*"],
            timeInfo: {
              startField: "recorded_at",
              endField: "recorded_at",
            },
            effect: "drop-shadow(1.5px, 1.5px, 2.25px, rgb(50,50,50))",
            renderer: waypointRenderer,
          });
          waypointsLayerRef.current = waypointsLayer;
        }

        // Outdoors vector tile layer
        const outdoorsLayer = new VectorTileLayer({
          portalItem: {
            id: "f320a8af9ffa4b4baeaf1d66f03da86f",
          },
        });

        // Trail segments layer (same as TrailMap)
        const trailSegmentsLayer = new GeoJSONLayer({
          url: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/trail-segments-geojson`,
          title: "Trail Segments",
          opacity: 0.6,
          renderer: {
            type: "simple",
            symbol: {
              type: "cim",
              data: {
                type: "CIMSymbolReference",
                symbol: {
                  type: "CIMLineSymbol",
                  symbolLayers: [
                    {
                      type: "CIMSolidStroke",
                      effects: [
                        {
                          type: "CIMGeometricEffectOffset",
                          method: "Bevelled",
                          offset: 0,
                          option: "Fast",
                        },
                        {
                          type: "CIMGeometricEffectDashes",
                          lineDashEnding: "NoConstraint",
                          controlPointEnding: "NoConstraint",
                        },
                      ],
                      enable: true,
                      colorLocked: true,
                      capStyle: "Round",
                      joinStyle: "Round",
                      lineStyle3D: "Strip",
                      miterLimit: 10,
                      width: 1,
                      color: [225, 137, 55, 255],
                      animations: [],
                    },
                    {
                      type: "CIMSolidStroke",
                      effects: [
                        {
                          type: "CIMGeometricEffectOffset",
                          method: "Mitered",
                          offset: 0,
                          option: "Fast",
                        },
                        {
                          type: "CIMGeometricEffectDashes",
                          lineDashEnding: "NoConstraint",
                          controlPointEnding: "NoConstraint",
                        },
                      ],
                      enable: true,
                      colorLocked: true,
                      capStyle: "Round",
                      joinStyle: "Round",
                      lineStyle3D: "Strip",
                      miterLimit: 10,
                      width: 2.5,
                      color: [112, 63, 26, 255],
                      animations: [],
                    },
                    {
                      type: "CIMSolidStroke",
                      effects: [
                        {
                          type: "CIMGeometricEffectOffset",
                          method: "Mitered",
                          offset: 0,
                          option: "Fast",
                        },
                        {
                          type: "CIMGeometricEffectDashes",
                          lineDashEnding: "NoConstraint",
                          controlPointEnding: "NoConstraint",
                        },
                      ],
                      enable: true,
                      colorLocked: true,
                      capStyle: "Round",
                      joinStyle: "Round",
                      lineStyle3D: "Strip",
                      miterLimit: 10,
                      width: 5,
                      color: [255, 255, 255, 125],
                      animations: [],
                    },
                  ],
                  animations: [],
                },
              },
            },
            visualVariables: [
              {
                type: "size",
                valueExpression: "$view.scale",
                stops: [
                  { size: 5.94 * 1.2, value: 2256.99 },
                  { size: 3.06 * 1.2, value: 18055.95 },
                  { size: 2.4 * 1.2, value: 144447.63 },
                  { size: 1.32 * 1.2, value: 1155581.1 },
                ],
              },
            ],
          } as any,
        });

        // Create graphics layer for current position marker
        const currentPositionLayer = new GraphicsLayer({
          title: "Current Position",
        });

        // BASEMAP TOGGLE FIX: Wrap outdoors layer in Basemap object
        // To revert: Remove outdoorsBasemap, put outdoorsLayer back in layers array, remove basemap property
        const outdoorsBasemap = new Basemap({
          baseLayers: [outdoorsLayer]
        });

        const map = new Map({
          basemap: outdoorsBasemap, // BASEMAP TOGGLE FIX: Set as basemap instead of layer
          layers: [trailSegmentsLayer, runTrackLayer, currentPositionLayer, waypointsLayer],
          // REVERT: layers: [outdoorsLayer, trailSegmentsLayer, runTrackLayer, currentPositionLayer, waypointsLayer],
        });

        // Calculate initial zoom from extent if provided
        let initialZoom = 12;
        if (initialExtent) {
          const { xmin, ymin, xmax, ymax } = initialExtent;
          const extentWidth = xmax - xmin;
          const extentHeight = ymax - ymin;
          const maxExtent = Math.max(extentWidth, extentHeight);
          // Approximate zoom level based on extent size
          initialZoom = Math.floor(14 - Math.log2(maxExtent / 0.01));
        }

        view = new MapView({
          container: mapDiv.current,
          map: map,
          center: initialCenter || [-117.465, 34.223],
          zoom: initialZoom,
          constraints: {
            rotationEnabled: false,
          },
          ui: {
            components: [],
          },
        });

        // Only add interactive widgets if map is interactive
        if (isInteractive) {
          // Add zoom control to bottom-right
          const zoomWidget = new Zoom({
            view: view,
          });
          view.ui.add(zoomWidget, "bottom-right");

          // Add basemap toggle above zoom controls
          const satelliteBasemap = new Basemap({
            portalItem: {
              id: "d802f08316e84c6592ef681c50178f17", // World Imagery basemap
            },
          });

          const basemapToggle = new BasemapToggle({
            view: view,
            nextBasemap: satelliteBasemap,
          });
          view.ui.add(basemapToggle, {
            position: "bottom-right",
            index: 0,
          });
        }

        // Configure navigation based on interactivity
        view.when(() => {
          if (!isInteractive) {
            // Disable all navigation for locked users
            if (view.navigation) {
              view.navigation.mouseWheelZoomEnabled = false;
              view.navigation.browserTouchPanEnabled = false;
            }
            // Disable pointer events for dragging/panning
            view.on("drag", (event: any) => event.stopPropagation());
            view.on("double-click", (event: any) => event.stopPropagation());
            view.on("key-down", (event: any) => event.stopPropagation());
          } else {
            // Only disable scroll zoom for better UX (current behavior)
            if (view.navigation) {
              view.navigation.mouseWheelZoomEnabled = false;
            }
            
            // Disable default popup for custom waypoint popups
            view.popup.autoOpenEnabled = false;
            
            // Add waypoint hover handler for interactive (paid) users
            view.on("pointer-move", (event: any) => {
              if (!view || view.destroyed || !waypointsLayerRef.current) return;
              
              view.hitTest(event).then((response: any) => {
                if (response?.results?.length > 0) {
                  const graphic = response.results.find(
                    (r: any) => r.graphic?.layer === waypointsLayerRef.current
                  );
                  
                  if (graphic) {
                    const attrs = graphic.graphic.attributes;
                    setHoverWaypoint({
                      name: attrs.name || getWaypointLabel(attrs.detection_type),
                      description: attrs.description,
                      detection_type: attrs.detection_type,
                      x: event.x,
                      y: event.y,
                    });
                  } else {
                    setHoverWaypoint(null);
                  }
                } else {
                  setHoverWaypoint(null);
                }
              }).catch(() => {
                // Silently ignore hitTest errors
              });
            });
            
            // Add waypoint click handler for custom popup
            view.on("click", async (event: any) => {
              if (!view || view.destroyed || !waypointsLayerRef.current) return;
              
              try {
                const response = await view.hitTest(event);
                
                if (response?.results?.length > 0) {
                  const graphic = response.results.find(
                    (r: any) => r.graphic?.layer === waypointsLayerRef.current
                  );
                  
                  if (graphic) {
                    const attrs = graphic.graphic.attributes;
                    setClickedWaypoint({
                      name: attrs.name || getWaypointLabel(attrs.detection_type),
                      description: attrs.description,
                      detection_type: attrs.detection_type,
                      photo_urls: attrs.photo_urls,
                      x: event.x,
                      y: event.y,
                    });
                    setHoverWaypoint(null); // Clear hover when clicking
                  } else {
                    setClickedWaypoint(null);
                  }
                } else {
                  setClickedWaypoint(null);
                }
              } catch {
                // Silently ignore hitTest errors
              }
            });
          }
        });

        // Wait for view to be fully loaded before starting animation
        view.when(() => {
          console.log("View is ready");
          // Wait for all layers to be ready
          Promise.all([
            view.whenLayerView(outdoorsLayer),
            view.whenLayerView(runTrackLayer),
            view.whenLayerView(waypointsLayer),
          ])
            .then(() => {
              console.log("All layers loaded in view");
              // Also wait for layer data to be ready
              return Promise.all([runTrackLayer.when(), waypointsLayer.when()]);
            })
            .then(() => {
              console.log("All layer data ready");
              setMapLoaded(true);
              setAnimationReady(true);
              // Add 1 second delay before starting animation
              setTimeout(() => {
                console.log("Run track layer loaded, starting animation");
                // Helper to set up and animate once we know the time extent
                const setupAndAnimate = (start: Date, end: Date) => {
                  // Collapse to the very start
                  view.timeExtent = { start, end: start };

                  // Only zoom to data if no initial extent was provided
                  if (!initialExtent) {
                    runTrackLayer.queryExtent().then((result: any) => {
                      if (result.extent) {
                        view.goTo(result.extent.expand(1.2));
                      }
                    });
                  }

                  // Pre-fetch ALL track features once for client-side filtering
                  const allFeaturesQuery: any = runTrackLayer.createQuery();
                  allFeaturesQuery.where = "1=1";
                  allFeaturesQuery.returnGeometry = true;
                  allFeaturesQuery.outFields = ["distance_miles", "start_timestamp", "end_timestamp"];
                  allFeaturesQuery.orderByFields = ["start_timestamp ASC"];

                  runTrackLayer.queryFeatures(allFeaturesQuery).then((res: any) => {
                    // Store features for client-side filtering during animation
                    trackFeaturesRef.current = res.features || [];

                    // Calculate total distance
                    let totalDistance = 0;
                    res.features?.forEach((feature: any) => {
                      totalDistance += feature.attributes.distance_miles || 0;
                    });
                    setDistance(totalDistance);
                  });

                  // Store waypoints data for tracking during animation
                  const waypointsWithTime = (waypoints || [])
                    .filter((wp: any) => wp.recorded_at)
                    .map((wp: any) => ({
                      ...wp,
                      recorded_at_ms: new Date(wp.recorded_at).getTime(),
                    }))
                    .sort((a: any, b: any) => a.recorded_at_ms - b.recorded_at_ms);
                  waypointsDataRef.current = waypointsWithTime;

                  // Animate based on timestamps
                  function animate() {
                    // Cancel any ongoing animation
                    if (animationRef.current) {
                      cancelAnimationFrame(animationRef.current);
                    }

                    setElapsedTime(0);
                    setProgress(0);
                    setDistance(0);
                    setShowReplay(false);
                    setCurrentWaypoint(null);
                    setShowWaypointPanel(false);
                    shownWaypointIdsRef.current = new Set(); // Reset shown waypoints
                    animationStartTimeRef.current = start.getTime();
                    runTrackLayer.opacity = 1; // show layer when animation starts
                    currentPositionLayer.removeAll(); // clear position marker on replay

                    const playbackSpeed = 900; // unitless multiplier (half speed for smoother animation)
                    const actualDurationMs = end.getTime() - start.getTime();
                    const animationDurationMs = actualDurationMs / playbackSpeed;
                    const maxAnimationDurationMs = 20000;
                    const animationDuration = Math.min(animationDurationMs, maxAnimationDurationMs);
                    const playbackStart = performance.now();

                    function step(now: number) {
                      const elapsed = now - playbackStart;
                      const progressValue = Math.min(elapsed / animationDuration, 1);
                      const current = new Date(start.getTime() + progressValue * (end.getTime() - start.getTime()));
                      const currentTime = current.getTime();

                      // Update elapsed time display (in seconds) and progress
                      const elapsedSeconds = Math.floor((progressValue * actualDurationMs) / 1000);
                      setElapsedTime(elapsedSeconds);
                      setProgress(progressValue * 100);

                      view.timeExtent = { start, end: current };

                      // Check for newly visible waypoints
                      const newlyVisibleWaypoint = waypointsDataRef.current.find((wp: any) => {
                        const isNowVisible = wp.recorded_at_ms <= currentTime;
                        const notYetShown = !shownWaypointIdsRef.current.has(wp.id);
                        return isNowVisible && notYetShown;
                      });

                      if (newlyVisibleWaypoint) {
                        // Mark as shown
                        shownWaypointIdsRef.current.add(newlyVisibleWaypoint.id);
                        
                        // Calculate elapsed time for this waypoint
                        const waypointElapsedMs = newlyVisibleWaypoint.recorded_at_ms - animationStartTimeRef.current;
                        const waypointElapsedSeconds = Math.floor(waypointElapsedMs / 1000);

                        // Update current waypoint for the info panel
                        setCurrentWaypoint({
                          id: newlyVisibleWaypoint.id,
                          name: newlyVisibleWaypoint.name || "Waypoint",
                          description: newlyVisibleWaypoint.description,
                          detection_type: newlyVisibleWaypoint.detection_type,
                          photo_urls: newlyVisibleWaypoint.photo_urls,
                          recorded_at: newlyVisibleWaypoint.recorded_at_ms,
                          elapsedSeconds: waypointElapsedSeconds,
                        });
                        setShowWaypointPanel(true);

                        // Auto-hide panel after 4 seconds
                        setTimeout(() => {
                          setShowWaypointPanel(false);
                        }, 4000);
                      }

                      // Update current position marker using pre-fetched features (synchronous, no query)
                      const visibleFeatures = trackFeaturesRef.current.filter((feature: any) => {
                        const featureStart = feature.attributes?.start_timestamp;
                        const featureEnd = feature.attributes?.end_timestamp;
                        return featureStart && featureEnd && featureStart <= currentTime && featureEnd <= currentTime;
                      });

                      if (visibleFeatures.length > 0) {
                        // Calculate cumulative distance using precalculated values
                        let cumulativeDistance = 0;
                        visibleFeatures.forEach((feature: any) => {
                          cumulativeDistance += feature.attributes.distance_miles || 0;
                        });
                        setDistance(cumulativeDistance);

                        // Get the last feature (most recent segment)
                        const lastFeature = visibleFeatures[visibleFeatures.length - 1];
                        if (lastFeature.geometry && lastFeature.geometry.paths && lastFeature.geometry.paths[0]) {
                          const lastPath = lastFeature.geometry.paths[0];
                          const lastPoint = lastPath[lastPath.length - 1];

                          // Clear previous graphics
                          currentPositionLayer.removeAll();

                          // Create circle graphic at current position
                          const pointGraphic = new Graphic({
                            geometry: {
                              type: "point",
                              longitude: lastPoint[0],
                              latitude: lastPoint[1],
                            },
                            symbol: {
                              type: "simple-marker",
                              size: 12,
                              color: [255, 198, 102, 1], // Match run track color
                              outline: {
                                color: [255, 255, 255, 0.9],
                                width: 2.5,
                              },
                            },
                          });

                          currentPositionLayer.add(pointGraphic);
                        }
                      }

                      if (progressValue < 1) {
                        animationRef.current = requestAnimationFrame(step);
                      } else {
                        setTimeout(() => {
                          fadeLayer(runTrackLayer, 0.9, 1000);
                          currentPositionLayer.removeAll();
                          setShowReplay(true);
                          setShowWaypointPanel(false);
                        }, 1000);
                      }
                    }
                    animationRef.current = requestAnimationFrame(step);
                  }

                  // Store animate function for replay
                  animateFunctionRef.current = animate;

                  // Delay animation start by 2 seconds
                  setTimeout(animate, 2000);
                };

                // Always query features for reliable time extent calculation
                // (fullTimeExtent from timeInfo is async and unreliably populated)
                const query: any = runTrackLayer.createQuery();
                query.where = "1=1";
                query.returnGeometry = false;
                query.outFields = ["start_timestamp", "end_timestamp"];
                runTrackLayer
                  .queryFeatures(query)
                  .then((res: any) => {
                    let min = Infinity;
                    let max = -Infinity;
                    res.features?.forEach((f: any) => {
                      const s = f.attributes?.start_timestamp;
                      const e = f.attributes?.end_timestamp;
                      if (typeof s === "number") min = Math.min(min, s);
                      if (typeof e === "number") max = Math.max(max, e);
                    });
                    if (isFinite(min) && isFinite(max)) {
                      setupAndAnimate(new Date(min), new Date(max));
                    } else {
                      console.warn("No valid time attributes found; showing static layer");
                      runTrackLayer.opacity = 1;
                    }
                  })
                  .catch((error: any) => {
                    console.error("Error querying features:", error);
                    runTrackLayer.opacity = 1;
                  });
              }, 1000); // 1-second delay after layers load
            })
            .catch((error: any) => {
              console.error("Error loading layers:", error);
              setMapLoaded(true);
            });
        });
      } catch (error) {
        console.error("Error initializing run map:", error);
      }
    };

    initializeMap();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (view) {
        view.destroy();
        view = null;
      }
    };
  }, [runId, initialCenter, initialExtent]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}`;
    }
    return `${minutes}`;
  };

  const handleReplay = () => {
    if (animateFunctionRef.current) {
      animateFunctionRef.current();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hero photo background overlay (optional) */}
      {heroPhotoUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 blur-sm"
          style={{ backgroundImage: `url(${heroPhotoUrl})` }}
        />
      )}

      <div ref={mapDiv} className="w-full h-full" />

      {/* Waypoint Info Panel - hidden for refinement
      <WaypointInfoPanel
        waypoint={currentWaypoint}
        isVisible={showWaypointPanel}
        onPhotoClick={onPhotoClick}
      />
      */}

      {/* Time display and replay button - only show when animation is ready */}
      {animationReady && (
        <div className="absolute bottom-4 left-4 flex items-center gap-3">
          <div className="bg-background/90 backdrop-blur-sm rounded-lg shadow-lg border overflow-hidden">
            <div className="px-4 py-2">
              <div className="text-xs font-medium text-muted-foreground">Travel Time</div>
              <div className="text-2xl font-bold tabular-nums">{formatTime(elapsedTime)}</div>
            </div>
            {/* Progress bar */}
            <div className="h-1 bg-muted">
              <div
                className="h-full bg-primary transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {showReplay && (
            <Button onClick={handleReplay} size="icon" className="h-12 w-12 shadow-lg" title="Replay animation">
              <RotateCcw className="h-5 w-5" />
            </Button>
          )}
        </div>
      )}

      {/* Waypoint hover tooltip - only for paid users */}
      {isInteractive && hoverWaypoint && !clickedWaypoint && (
        <div
          className="absolute z-50 px-3 py-2 bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg pointer-events-none max-w-xs"
          style={{ 
            left: `${Math.min(hoverWaypoint.x + 12, (mapDiv.current?.clientWidth || 300) - 200)}px`, 
            top: `${hoverWaypoint.y + 12}px` 
          }}
        >
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 text-primary">
              {getWaypointIcon(hoverWaypoint.detection_type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                {getWaypointLabel(hoverWaypoint.detection_type)}
              </p>
              <p className="text-sm font-semibold truncate">{hoverWaypoint.name}</p>
            </div>
          </div>
        </div>
      )}

      {/* Waypoint click popup - only for paid users */}
      {isInteractive && clickedWaypoint && (
        <div
          className="absolute z-50 bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-xl max-w-xs"
          style={{ 
            left: `${Math.min(clickedWaypoint.x + 12, (mapDiv.current?.clientWidth || 300) - 220)}px`, 
            top: `${Math.min(clickedWaypoint.y + 12, (mapDiv.current?.clientHeight || 300) - 180)}px` 
          }}
        >
          {/* Close button */}
          <button 
            onClick={() => setClickedWaypoint(null)}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-background border shadow-sm flex items-center justify-center hover:bg-muted z-10"
          >
            <X className="h-3 w-3" />
          </button>
          
          <div className="p-3">
            {/* Header with icon and type */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary">
                {getWaypointIcon(clickedWaypoint.detection_type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                  {getWaypointLabel(clickedWaypoint.detection_type)}
                </p>
                <p className="text-sm font-semibold truncate">
                  {clickedWaypoint.name}
                </p>
              </div>
            </div>

            {/* Photo thumbnail */}
            {clickedWaypoint.photo_urls?.[0] && (
              <button
                onClick={() => onPhotoClick?.(clickedWaypoint.photo_urls![0])}
                className="relative w-full h-24 rounded-md overflow-hidden mb-2 hover:opacity-90"
              >
                <img
                  src={clickedWaypoint.photo_urls[0]}
                  alt={clickedWaypoint.name}
                  className="w-full h-full object-cover"
                />
                {clickedWaypoint.photo_urls.length > 1 && (
                  <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    +{clickedWaypoint.photo_urls.length - 1}
                  </div>
                )}
              </button>
            )}

            {/* Description */}
            {clickedWaypoint.description && (
              <p className="text-xs text-muted-foreground line-clamp-3">
                {clickedWaypoint.description}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Lock badge for non-interactive maps */}
      {!isInteractive && (
        <div className="absolute bottom-4 right-4 z-20">
          <Badge variant="secondary" className="flex items-center gap-1.5 bg-background/80 backdrop-blur-sm px-3 py-1.5">
            <Lock className="h-3 w-3" />
            <span className="text-xs">Upgrade to explore</span>
          </Badge>
        </div>
      )}
    </div>
  );
};

export default RunHeroMap;
